import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  CLEAR_EDITOR_COMMAND,
  CLEAR_HISTORY_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
} from 'lexical';
import { REMOVE_LIST_COMMAND } from '@lexical/list';

import { Box } from '../../../../Box';
import { Button } from '../../../../Button';
import { Icon } from '../../../../Icon';

import useStyles from './SendButton.styles';

interface SendFormPluginProps {
  handleSendClick:
    | (() => Promise<'loading' | 'complete' | 'failed'>)
    | undefined;
  hasContent: boolean;
  isSubmitting?: boolean;
}

export const SendFormPlugin = ({
  handleSendClick,
  hasContent,
  isSubmitting = false,
}: SendFormPluginProps) => {
  const { classes } = useStyles();
  const [editor] = useLexicalComposerContext();

  const onSend = async () => {
    let actionStatus: 'complete' | 'failed' | 'loading' = 'complete';
    if (handleSendClick) {
      const status = await handleSendClick();
      actionStatus = status;
    }

    if (actionStatus === 'failed') {
      return;
    }

    editor.update(() => {
      const selection = $getSelection();

      const root = $getRoot();
      root.clear();

      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');

      if ($isRangeSelection(selection)) {
        if (selection.hasFormat('bold'))
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        if (selection.hasFormat('italic'))
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        if (selection.hasFormat('underline'))
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        if (selection.hasFormat('strikethrough'))
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
      }
    });
  };

  return (
    <Box pl="16">
      <Button
        className={classes.buttonPrimaryIconOnly}
        disabled={!hasContent || isSubmitting}
        onClick={onSend}
        size="large"
        variant="primary"
        icon={
          <Box display="flex" alignItems="center">
            <Icon
              name="send_outline"
              size="24"
              color={
                !hasContent || isSubmitting
                  ? 'text.light.tertirary'
                  : 'bg.light.primary'
              }
            />
          </Box>
        }
      />
    </Box>
  );
};
